// Define or edit an automatic promotion (F-POS-03, spec/06 §8.4) in four short steps, never one long
// form: what it applies to → the discount → when and limits → confirm. The fields are the contract's
// (`packages/contracts/src/routes/promotions.ts`) and nothing else: line kinds, a product set, venues,
// channels and the first-order rule; `percent` (basis points) or `amount` (minor units) with an
// optional cap, stackable and a priority; days of the week, a happy-hour window in minutes of the
// venue's day, a minimum order, a validity window and whether it is on.
//
// Editing sends only what changed (`updatePromotion`); a promotion's type cannot change after it is made,
// so the type is fixed while editing. Money is typed in rupees and sent as integer paise.
//
// **Play time** (`play`, pay-as-you-go and wallet play) is a kind of its own: a session's play is discounted
// unit by unit as each billing unit starts — a happy hour on play. It stands alone (choosing it clears the
// sale kinds, and choosing a sale kind clears it), it is always a percentage, and products, channels, a
// minimum order and the first-order rule do not apply to it, so they are hidden and sent empty — the
// server refuses a play promotion otherwise. Its cap is per session.
//
// **Console sessions** (`console`, ADR-0034) are a kind of their own too: a console offer discounts a staff-run
// session's one order. Choosing it swaps the cart's rules for the console's conditions — at least N controllers, at
// least X minutes, the station classes, players only — and the discount step for its reward: a share or a sum off the
// extra controllers, a bundle price, free time, or a share off the session. Its day, time window, venues, validity,
// cap, priority and stacking are the same fields as any promotion's.

import type { ConsoleOffer, CreatePromotionRequest, MenuProduct, Promotion, PromotionKind, PromotionRules, SaleChannel, UpdatePromotionRequest } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Chip, FormSection, SegmentedControl, Stepper, Switch, TextField, useToast } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { useApi } from '../../api/client';
import { useMyVenues } from '../../api/queries';
import { useScopeIds } from '../../shell/scope';
import { useStationClasses } from '../../stations/queries';
import { Facts, Notice } from '../../ui';
import { minorToMajor, moneyFrom } from '../money';
import { useServerErrors } from '../server-errors';
import { useCreatePromotion, useUpdatePromotion } from './queries';
import { bpsToPercent, consoleOfferWords, endOfDayIso, minuteOfDay, percentToBps, startOfDayIso, timeOfMinute, valueWords, venueDateOf } from './terms';

/** What a promotion discounts in the form: a sale-line kind, play time, or console sessions (ADR-0034). */
type KindChoice = PromotionKind | 'console';
/** The sale-line kinds, then play time and console sessions — each of which stands alone. */
const KINDS: readonly KindChoice[] = ['time', 'food', 'retail', 'other', 'play', 'console'];
const PLAY: PromotionKind = 'play';
const CONSOLE = 'console' as const;
const CONSOLE_REWARDS = ['extra_controllers_percent', 'extra_controllers_amount', 'bundle', 'free_time', 'session_percent'] as const;
type ConsoleReward = (typeof CONSOLE_REWARDS)[number];
const CHANNELS: readonly SaleChannel[] = ['counter', 'kiosk', 'app', 'web'];
/** The contract's days: 0 is Sunday. Shown Monday first, the way the venue's week reads. */
const WEEK: readonly number[] = [1, 2, 3, 4, 5, 6, 0];
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

interface FormState {
  name: string;
  kinds: readonly KindChoice[];
  someProducts: boolean;
  productIds: readonly string[];
  /** Every venue, or the ones listed. */
  everywhere: boolean;
  venueIds: readonly string[];
  channels: readonly SaleChannel[];
  firstOrderOnly: boolean;
  type: 'percent' | 'amount' | 'console';
  percent: string;
  amount: string;
  cap: string;
  stackable: boolean;
  priority: string;
  days: readonly number[];
  happyHour: boolean;
  start: string;
  end: string;
  minOrder: string;
  validFrom: string;
  validUntil: string;
  active: boolean;
  // A console offer (ADR-0034): its conditions and reward.
  reward: ConsoleReward;
  bundleControllers: string;
  rewardMinutes: string;
  freeMinutes: string;
  minControllers: string;
  minDuration: string;
  classIds: readonly string[];
  playersOnly: boolean;
}

const STEP_FIELDS: readonly (readonly string[])[] = [['name', 'kinds', 'venues', 'products', 'conditions'], ['value', 'cap', 'priority'], ['days', 'window', 'minOrder', 'dates'], ['form']];

/** The request's fields -> the form's inputs, for a validation failure. */
const PROMOTION_SERVER_FIELDS: Readonly<Record<string, string>> = {
  name: 'name',
  percentBps: 'value',
  amount: 'value',
  maxDiscountAmount: 'cap',
  priority: 'priority',
  validFrom: 'dates',
  validUntil: 'dates',
};

function formFor(editing: Promotion | undefined, venueId: string | undefined, currency: string, timezone: string): FormState {
  if (editing === undefined) {
    return {
      name: '',
      kinds: [],
      someProducts: false,
      productIds: [],
      everywhere: venueId === undefined,
      venueIds: venueId === undefined ? [] : [venueId],
      channels: [],
      firstOrderOnly: false,
      type: 'percent',
      percent: '',
      amount: '',
      cap: '',
      stackable: false,
      priority: '0',
      days: [],
      happyHour: false,
      start: '17:00',
      end: '20:00',
      minOrder: '',
      validFrom: '',
      validUntil: '',
      active: true,
      reward: 'extra_controllers_percent',
      bundleControllers: '',
      rewardMinutes: '',
      freeMinutes: '',
      minControllers: '',
      minDuration: '',
      classIds: [],
      playersOnly: false,
    };
  }
  const rules = editing.rules;
  const offer = editing.console;
  return {
    name: editing.name,
    kinds: editing.type === 'console' ? [CONSOLE] : (rules.appliesToKinds ?? []),
    someProducts: (rules.productIds ?? []).length > 0,
    productIds: rules.productIds ?? [],
    everywhere: (rules.venueIds ?? []).length === 0,
    venueIds: rules.venueIds ?? [],
    channels: rules.channels ?? [],
    firstOrderOnly: rules.firstOrderOnly ?? false,
    type: editing.type,
    percent: offer?.percentBps != null ? bpsToPercent(offer.percentBps) : editing.percentBps === null ? '' : bpsToPercent(editing.percentBps),
    amount: offer?.amount != null ? minorToMajor(offer.amount.amount, offer.amount.currency) : editing.amount === null ? '' : minorToMajor(editing.amount.amount, editing.amount.currency),
    cap: editing.maxDiscountAmount === null ? '' : minorToMajor(editing.maxDiscountAmount.amount, editing.maxDiscountAmount.currency),
    stackable: editing.stackable,
    priority: String(editing.priority),
    days: rules.daysOfWeek ?? [],
    happyHour: rules.startMinuteOfDay != null && rules.endMinuteOfDay != null,
    start: rules.startMinuteOfDay == null ? '17:00' : timeOfMinute(rules.startMinuteOfDay),
    end: rules.endMinuteOfDay == null ? '20:00' : timeOfMinute(rules.endMinuteOfDay),
    minOrder: rules.minOrderAmount == null ? '' : minorToMajor(rules.minOrderAmount.amount, rules.minOrderAmount.currency ?? currency),
    validFrom: editing.validFrom === null ? '' : venueDateOf(editing.validFrom, timezone),
    validUntil: editing.validUntil === null ? '' : venueDateOf(editing.validUntil, timezone),
    active: editing.active,
    reward: offer?.reward ?? 'extra_controllers_percent',
    bundleControllers: offer?.controllers == null ? '' : String(offer.controllers),
    rewardMinutes: offer?.minutes == null ? '' : String(offer.minutes),
    freeMinutes: offer?.freeMinutes == null ? '' : String(offer.freeMinutes),
    minControllers: offer?.minControllers == null ? '' : String(offer.minControllers),
    minDuration: offer?.minDurationMinutes == null ? '' : String(offer.minDurationMinutes),
    classIds: offer?.stationClassIds ?? [],
    playersOnly: offer?.playersOnly ?? false,
  };
}

/** A whole number in `min`…`max` from a typed string, or null when it is not one. */
function wholeIn(value: string, min: number, max: number): number | null {
  const number = Number(value);
  return value.trim() !== '' && Number.isInteger(number) && number >= min && number <= max ? number : null;
}

/** The console offer a form state stands for (ADR-0034): only the fields its reward uses are sent. */
function consoleOfferOf(form: FormState, currency: string): ConsoleOffer {
  const percent = form.reward === 'extra_controllers_percent' || form.reward === 'session_percent';
  const amount = form.reward === 'extra_controllers_amount' || form.reward === 'bundle';
  return {
    minControllers: form.minControllers.trim() === '' ? null : wholeIn(form.minControllers, 1, 16),
    minDurationMinutes: form.minDuration.trim() === '' ? null : wholeIn(form.minDuration, 1, 1440),
    stationClassIds: [...form.classIds],
    playersOnly: form.playersOnly,
    reward: form.reward,
    percentBps: percent ? percentToBps(form.percent) : null,
    amount: amount ? moneyFrom(form.amount, currency) : null,
    controllers: form.reward === 'bundle' ? wholeIn(form.bundleControllers, 1, 16) : null,
    minutes: form.reward === 'bundle' || form.reward === 'free_time' ? wholeIn(form.rewardMinutes, 1, 1440) : null,
    freeMinutes: form.reward === 'free_time' ? wholeIn(form.freeMinutes, 1, 1440) : null,
  };
}

export function PromotionFlow({ editing, onDone }: { readonly editing?: Promotion; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const { show } = useToast();
  const serverErrors = useServerErrors();
  const api = useApi();
  const ids = useScopeIds();
  const currency = ids.currency ?? 'INR';
  const myVenues = useMyVenues();
  const venues = useMemo(() => (myVenues.data ?? []).filter((venue) => venue.organizationId === ids.organizationId), [myVenues.data, ids.organizationId]);
  const timezone = venues.find((venue) => venue.id === ids.venueId)?.timezone ?? venues[0]?.timezone ?? 'Asia/Kolkata';
  const create = useCreatePromotion(ids.organizationId);
  const update = useUpdatePromotion(ids.organizationId);
  // A console offer's class condition reads the venue's classes; with no venue in scope it applies to every class.
  const classes = useStationClasses(ids.organizationId, ids.venueId ?? '');

  const initial = useMemo(() => formFor(editing, ids.venueId, currency, timezone), [editing, ids.venueId, currency, timezone]);
  const [form, setForm] = useState<FormState>(initial);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const products = useQuery({
    queryKey: ['pricing', ids.organizationId, 'products'],
    queryFn: async () => (await api.listMenuProducts({ path: { organizationId: ids.organizationId } })).products,
    enabled: form.someProducts,
    retry: false,
  });

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((current) => ({ ...current, [key]: value }));
  }
  function toggle<T>(list: readonly T[], item: T): T[] {
    return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
  }
  /**
   * Choosing what it discounts. Play time stands alone: choosing it clears the sale kinds and what does not
   * apply to play (products, channels, a minimum order, first order only) and makes it a percentage;
   * choosing a sale kind clears play.
   */
  function toggleKind(kind: KindChoice): void {
    setForm((current) => {
      // Console sessions stand alone (ADR-0034): no cart rules, and its own type — fixed once the offer is made.
      if (kind === CONSOLE) {
        if (current.kinds.includes(CONSOLE)) return editing === undefined ? { ...current, kinds: [], type: 'percent' as const } : { ...current, kinds: [CONSOLE] };
        if (editing !== undefined && editing.type !== 'console') return current;
        return { ...current, kinds: [CONSOLE], someProducts: false, productIds: [], channels: [], firstOrderOnly: false, minOrder: '', type: 'console' as const };
      }
      if (editing?.type === 'console') return current;
      if (kind !== PLAY) {
        const kinds = toggle(current.kinds.filter((one) => one !== PLAY && one !== CONSOLE), kind);
        return { ...current, kinds, ...(current.type === 'console' ? { type: 'percent' as const } : {}) };
      }
      if (current.kinds.includes(PLAY)) return { ...current, kinds: [] };
      return {
        ...current,
        kinds: [PLAY],
        ...(current.type === 'console' ? { type: 'percent' as const } : {}),
        someProducts: false,
        productIds: [],
        channels: [],
        firstOrderOnly: false,
        minOrder: '',
        ...(editing === undefined ? { type: 'percent' as const } : {}),
      };
    });
  }
  const isPlay = form.kinds.includes(PLAY);
  const isConsole = form.kinds.includes(CONSOLE);

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (form.name.trim() === '') next['name'] = t('pricing.form.required');
    if (!form.everywhere && form.venueIds.length === 0) next['venues'] = t('pricing.promotionFlow.venueRequired');
    // A made promotion keeps its type: an amount one cannot become play time, which is a percentage.
    if (form.kinds.includes(PLAY) && form.type !== 'percent') next['kinds'] = t('pricing.promotionFlow.playNeedsPercent');
    if (form.someProducts && form.productIds.length === 0) next['products'] = t('pricing.promotionFlow.productRequired');
    if (isConsole) {
      const offer = consoleOfferOf(form, currency);
      if (form.minControllers.trim() !== '' && offer.minControllers === null) next['conditions'] = t('pricing.console.form.controllersRange');
      if (form.minDuration.trim() !== '' && offer.minDurationMinutes === null) next['conditions'] = t('pricing.console.form.minutesRange');
      const percentReward = form.reward === 'extra_controllers_percent' || form.reward === 'session_percent';
      if (percentReward && offer.percentBps === null) next['value'] = t('pricing.form.percentInvalid');
      if ((form.reward === 'extra_controllers_amount' || form.reward === 'bundle') && (offer.amount === null || offer.amount.amount <= 0)) next['value'] = t('pricing.form.amountInvalid');
      if (form.reward === 'bundle' && (offer.controllers === null || offer.minutes === null)) next['value'] = t('pricing.console.form.bundleIncomplete');
      if (form.reward === 'free_time' && (offer.minutes === null || offer.freeMinutes === null)) next['value'] = t('pricing.console.form.freeIncomplete');
    }
    if (!isConsole && form.type === 'percent' && percentToBps(form.percent) === null) next['value'] = t('pricing.form.percentInvalid');
    if (!isConsole && form.type === 'amount') {
      const amount = moneyFrom(form.amount, currency);
      if (amount === null || amount.amount <= 0) next['value'] = t('pricing.form.amountInvalid');
    }
    if (form.cap.trim() !== '' && moneyFrom(form.cap, currency) === null) next['cap'] = t('pricing.form.amountInvalid');
    const priority = Number(form.priority);
    if (!Number.isInteger(priority) || priority < 0 || priority > 1000) next['priority'] = t('pricing.promotionFlow.priorityRange');
    if (form.happyHour && (minuteOfDay(form.start) === null || minuteOfDay(form.end) === null)) next['window'] = t('pricing.promotionFlow.windowInvalid');
    if (form.minOrder.trim() !== '' && moneyFrom(form.minOrder, currency) === null) next['minOrder'] = t('pricing.form.amountInvalid');
    if (form.validFrom !== '' && form.validUntil !== '' && form.validUntil < form.validFrom) next['dates'] = t('pricing.form.datesOrder');
    return next;
  }

  const rules = (): PromotionRules => rulesOf(form, currency);

  function next(): void {
    const all = validate();
    const mine = Object.fromEntries(Object.entries(all).filter(([key]) => STEP_FIELDS[step]?.includes(key)));
    setErrors(mine);
    if (Object.keys(mine).length === 0) setStep(step + 1);
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (step < 3) {
      next();
      return;
    }
    const all = validate();
    setErrors(all);
    if (Object.keys(all).length > 0) {
      const first = STEP_FIELDS.findIndex((fields) => fields.some((field) => field in all));
      if (first >= 0) setStep(first);
      return;
    }
    const cap = form.cap.trim() === '' ? null : moneyFrom(form.cap, currency);
    const validFrom = form.validFrom === '' ? null : startOfDayIso(form.validFrom, timezone);
    const validUntil = form.validUntil === '' ? null : endOfDayIso(form.validUntil, timezone);
    const percentBps = form.type === 'percent' ? percentToBps(form.percent) : null;
    const amount = form.type === 'amount' ? moneyFrom(form.amount, currency) : null;
    const offer = isConsole ? consoleOfferOf(form, currency) : null;
    const onError = (failure: unknown) => {
      const refused = serverErrors(failure, t('pricing.promotionFlow.refused'), PROMOTION_SERVER_FIELDS);
      setErrors(refused);
      const first = STEP_FIELDS.findIndex((fields) => fields.some((field) => field in refused));
      if (first >= 0) setStep(first);
    };

    if (editing !== undefined) {
      const patch: UpdatePromotionRequest = {};
      if (form.name.trim() !== initial.name.trim()) patch.name = form.name.trim();
      if (form.type === 'percent' && form.percent !== initial.percent) patch.percentBps = percentBps;
      if (form.type === 'amount' && form.amount !== initial.amount) patch.amount = amount;
      if (form.cap !== initial.cap) patch.maxDiscountAmount = cap;
      if (offer !== null && JSON.stringify(offer) !== JSON.stringify(consoleOfferOf(initial, currency))) patch.console = offer;
      if (JSON.stringify(rules()) !== JSON.stringify(rulesOf(initial, currency))) patch.rules = rules();
      if (form.priority !== initial.priority) patch.priority = Number(form.priority);
      if (form.stackable !== initial.stackable) patch.stackable = form.stackable;
      if (form.active !== initial.active) patch.active = form.active;
      if (form.validFrom !== initial.validFrom) patch.validFrom = validFrom;
      if (form.validUntil !== initial.validUntil) patch.validUntil = validUntil;
      if (Object.keys(patch).length === 0) {
        setErrors({ form: t('pricing.form.noChanges') });
        return;
      }
      update.mutate(
        { promotionId: editing.id, body: patch },
        {
          onSuccess: () => {
            show({ tone: 'ok', title: t('pricing.promotionFlow.saved', { name: form.name.trim() }) });
            onDone();
          },
          onError,
        },
      );
      return;
    }

    const body: CreatePromotionRequest = {
      name: form.name.trim(),
      type: form.type,
      ...(percentBps === null ? {} : { percentBps }),
      ...(amount === null ? {} : { amount }),
      ...(cap === null ? {} : { maxDiscountAmount: cap }),
      ...(offer === null ? {} : { console: offer }),
      rules: rules(),
      priority: Number(form.priority),
      stackable: form.stackable,
      active: form.active,
      ...(validFrom === null ? {} : { validFrom }),
      ...(validUntil === null ? {} : { validUntil }),
    };
    create.mutate(body, {
      onSuccess: () => {
        show({ tone: 'ok', title: t('pricing.promotionFlow.created', { name: body.name }) });
        onDone();
      },
      onError,
    });
  }

  const previewValue = isConsole
    ? consoleOfferWords(t, format.money, consoleOfferOf(form, currency), form.cap.trim() === '' ? null : moneyFrom(form.cap, currency))
    : valueWords(
        t,
        format.money,
        form.type === 'console' ? 'percent' : form.type,
        percentToBps(form.percent),
        form.type === 'amount' ? moneyFrom(form.amount, currency) : null,
        form.cap.trim() === '' ? null : moneyFrom(form.cap, currency),
        isPlay,
      );

  return (
    <div className="console-stepper-flow">
      <Stepper
        current={step}
        onStepSelect={(index) => (index < step ? setStep(index) : undefined)}
        steps={[
          { id: 'what', label: t('pricing.promotionFlow.stepWhat') },
          { id: 'discount', label: t('pricing.promotionFlow.stepDiscount') },
          { id: 'when', label: t('pricing.promotionFlow.stepWhen') },
          { id: 'confirm', label: t('pricing.promotionFlow.stepConfirm') },
        ]}
      />
      <form className="console-form" onSubmit={submit} noValidate>
        {errors['form'] !== undefined ? (
          <Notice tone="danger" role="alert">
            {errors['form']}
          </Notice>
        ) : null}

        {step === 0 ? (
          <FormSection key="what"
            more={
              isPlay || isConsole ? undefined : (
              <>
                <fieldset className="console-choice-group">
                  <legend className="clav-field__label">{t('pricing.promotionFlow.channels')}</legend>
                  <div className="console-chips">
                    {CHANNELS.map((channel) => (
                      <Chip key={channel} selected={form.channels.includes(channel)} onSelectedChange={() => set('channels', toggle(form.channels, channel))}>
                        {t(`pricing.channel.${channel}`)}
                      </Chip>
                    ))}
                  </div>
                  <p className="clav-field__description">{t('pricing.promotionFlow.channelsHint')}</p>
                </fieldset>
                <Switch checked={form.firstOrderOnly} onCheckedChange={(value) => set('firstOrderOnly', value)} label={t('pricing.promotionFlow.firstOrder')} />
              </>
            )
            }
          >
            <TextField label={t('pricing.promotionFlow.name')} description={t('pricing.promotionFlow.nameHint')} required value={form.name} onChange={(e) => set('name', e.target.value)} error={errors['name']} />
            <fieldset className="console-choice-group">
              <legend className="clav-field__label">{t('pricing.promotionFlow.kinds')}</legend>
              <div className="console-chips">
                {KINDS.map((kind) => (
                  <Chip key={kind} selected={form.kinds.includes(kind)} onSelectedChange={() => toggleKind(kind)}>
                    {t(`pricing.kind.${kind}`)}
                  </Chip>
                ))}
              </div>
              <p className="clav-field__description">{isConsole ? t('pricing.console.hint') : isPlay ? t('pricing.promotionFlow.playHint') : t('pricing.promotionFlow.kindsHint')}</p>
              {errors['kinds'] !== undefined ? <p className="clav-field__error">{errors['kinds']}</p> : null}
            </fieldset>
            {isConsole ? (
              <fieldset className="console-choice-group">
                <legend className="clav-field__label">{t('pricing.console.form.conditions')}</legend>
                <div className="console-form__row">
                  <TextField label={t('pricing.console.form.minControllers')} showOptional numeric inputMode="numeric" value={form.minControllers} onChange={(e) => set('minControllers', e.target.value)} />
                  <TextField label={t('pricing.console.form.minDuration')} showOptional numeric inputMode="numeric" value={form.minDuration} onChange={(e) => set('minDuration', e.target.value)} />
                </div>
                {errors['conditions'] !== undefined ? <p className="clav-field__error">{errors['conditions']}</p> : null}
                {ids.venueId !== undefined && (classes.data ?? []).length > 0 ? (
                  <>
                    <span className="clav-field__label">{t('pricing.console.form.classes')}</span>
                    <div className="console-chips">
                      {(classes.data ?? []).map((klass) => (
                        <Chip key={klass.id} selected={form.classIds.includes(klass.id)} onSelectedChange={() => set('classIds', toggle(form.classIds, klass.id))}>
                          {klass.name}
                        </Chip>
                      ))}
                    </div>
                    <p className="clav-field__description">{t('pricing.console.form.classesHint')}</p>
                  </>
                ) : null}
                <Switch checked={form.playersOnly} onCheckedChange={(value) => set('playersOnly', value)} label={t('pricing.console.form.playersOnly')} />
              </fieldset>
            ) : isPlay ? (
              <p className="console-subtle">{t('pricing.promotionFlow.playNotApplicable')}</p>
            ) : (
              <fieldset className="console-choice-group">
                <legend className="clav-field__label">{t('pricing.promotionFlow.products')}</legend>
                <Switch checked={form.someProducts} onCheckedChange={(value) => set('someProducts', value)} label={t('pricing.promotionFlow.someProducts')} />
                {form.someProducts ? (
                  products.data === undefined ? (
                    <p className="console-subtle">{products.isError ? t('pricing.promotionFlow.productsUnavailable') : t('pricing.promotionFlow.productsLoading')}</p>
                  ) : (
                    <ProductChips products={products.data} selected={form.productIds} onToggle={(id) => set('productIds', toggle(form.productIds, id))} />
                  )
                ) : null}
                {errors['products'] !== undefined ? <p className="clav-field__error">{errors['products']}</p> : null}
              </fieldset>
            )}
            <fieldset className="console-choice-group">
              <legend className="clav-field__label">{t('pricing.promotionFlow.where')}</legend>
              <Switch checked={form.everywhere} onCheckedChange={(value) => set('everywhere', value)} label={t('pricing.promotionFlow.everywhere')} />
              {form.everywhere ? null : (
                <div className="console-chips">
                  {venues.map((venue) => (
                    <Chip key={venue.id} selected={form.venueIds.includes(venue.id)} onSelectedChange={() => set('venueIds', toggle(form.venueIds, venue.id))}>
                      {venue.name}
                    </Chip>
                  ))}
                </div>
              )}
              {errors['venues'] !== undefined ? <p className="clav-field__error">{errors['venues']}</p> : null}
            </fieldset>
          </FormSection>
        ) : step === 1 ? (
          <FormSection key="discount"
            forceOpen={errors['priority'] !== undefined}
            more={
              <>
            <Switch checked={form.stackable} onCheckedChange={(value) => set('stackable', value)} label={t('pricing.promotionFlow.stackable')} />
            <p className="clav-field__description">{t('pricing.promotionFlow.stackableHint')}</p>
            <TextField label={t('pricing.promotionFlow.priority')} description={t('pricing.promotionFlow.priorityHint')} numeric inputMode="numeric" value={form.priority} onChange={(e) => set('priority', e.target.value)} error={errors['priority']} />
              </>
            }
          >
            {isConsole ? (
              <ConsoleRewardFields form={form} set={set} currency={currency} error={errors['value']} />
            ) : isPlay ? (
              <p className="console-subtle">{t('pricing.promotionFlow.playPercentOnly')}</p>
            ) : editing === undefined ? (
              <SegmentedControl<'percent' | 'amount'>
                label={t('pricing.form.type')}
                value={form.type === 'console' ? 'percent' : form.type}
                onValueChange={(value) => set('type', value)}
                block
                options={[
                  { value: 'percent', label: t('pricing.form.typePercent') },
                  { value: 'amount', label: t('pricing.form.typeAmount', { currency }) },
                ]}
              />
            ) : (
              <p className="console-subtle">{t('pricing.promotionFlow.typeFixed')}</p>
            )}
            {isConsole ? null : form.type === 'percent' ? (
              <TextField label={t('pricing.form.percent')} required numeric inputMode="decimal" value={form.percent} onChange={(e) => set('percent', e.target.value)} error={errors['value']} />
            ) : (
              <TextField label={t('pricing.form.amount', { currency })} required numeric inputMode="decimal" value={form.amount} onChange={(e) => set('amount', e.target.value)} error={errors['value']} />
            )}
            <TextField
              label={isPlay || isConsole ? t('pricing.promotionFlow.capSession', { currency }) : t('pricing.form.cap', { currency })}
              description={isConsole ? t('pricing.console.form.capHint') : isPlay ? t('pricing.promotionFlow.capSessionHint') : t('pricing.form.capHint')}
              showOptional
              numeric
              inputMode="decimal"
              value={form.cap}
              onChange={(e) => set('cap', e.target.value)}
              error={errors['cap']}
            />
          </FormSection>
        ) : step === 2 ? (
          <FormSection key="when"
            forceOpen={errors['minOrder'] !== undefined}
            more={
              <>
            {isPlay || isConsole ? null : (
              <TextField label={t('pricing.form.minOrder', { currency })} showOptional numeric inputMode="decimal" value={form.minOrder} onChange={(e) => set('minOrder', e.target.value)} error={errors['minOrder']} />
            )}
            <Switch checked={form.active} onCheckedChange={(value) => set('active', value)} label={t('pricing.promotionFlow.active')} />
              </>
            }
          >
            <fieldset className="console-choice-group">
              <legend className="clav-field__label">{t('pricing.promotionFlow.days')}</legend>
              <div className="console-chips console-chips--days">
                {WEEK.map((day) => (
                  <Chip key={day} selected={form.days.includes(day)} onSelectedChange={() => set('days', toggle(form.days, day))}>
                    {t(`passes.day.${DAY_KEYS[day] ?? 'mon'}`)}
                  </Chip>
                ))}
              </div>
              <p className="clav-field__description">{t('pricing.promotionFlow.daysHint')}</p>
            </fieldset>
            <fieldset className="console-choice-group">
              <legend className="clav-field__label">{t('pricing.promotionFlow.window')}</legend>
              <Switch checked={form.happyHour} onCheckedChange={(value) => set('happyHour', value)} label={t('pricing.promotionFlow.happyHour')} />
              {form.happyHour ? (
                <>
                  <div className="console-form__row">
                    <TextField label={t('pricing.promotionFlow.start')} type="time" value={form.start} onChange={(e) => set('start', e.target.value)} />
                    <TextField label={t('pricing.promotionFlow.end')} type="time" value={form.end} onChange={(e) => set('end', e.target.value)} />
                  </div>
                  {errors['window'] !== undefined ? <p className="clav-field__error">{errors['window']}</p> : null}
                  <p className="clav-field__description">{t('pricing.promotionFlow.windowHint')}</p>
                </>
              ) : null}
            </fieldset>
            <div className="console-form__row">
              <TextField label={t('pricing.form.validFrom')} type="date" showOptional value={form.validFrom} onChange={(e) => set('validFrom', e.target.value)} />
              <TextField label={t('pricing.form.validUntil')} type="date" showOptional value={form.validUntil} onChange={(e) => set('validUntil', e.target.value)} error={errors['dates']} />
            </div>
          </FormSection>
        ) : (
          <section className="console-dialog-preview" aria-label={t('pricing.promotionFlow.summary')}>
            <span className="console-eyebrow">{t('pricing.promotionFlow.summary')}</span>
            <p className="console-promo-preview__name">{form.name.trim()}</p>
            <p className="console-promo-preview__value">{previewValue}</p>
            <Facts
              columns={1}
              items={[
                { key: 'what', label: t('pricing.promotionFlow.kinds'), value: form.kinds.length === 0 ? t('pricing.rules.everything') : form.kinds.map((kind) => t(`pricing.kind.${kind}`)).join(', ') },
                {
                  key: 'where',
                  label: t('pricing.promotionFlow.where'),
                  value: form.everywhere ? t('pricing.rules.allVenues') : venues.filter((venue) => form.venueIds.includes(venue.id)).map((venue) => venue.name).join(', '),
                },
                {
                  key: 'when',
                  label: t('pricing.promotionFlow.days'),
                  value:
                    (form.days.length === 0 ? t('pricing.promotionFlow.everyDay') : WEEK.filter((day) => form.days.includes(day)).map((day) => t(`passes.day.${DAY_KEYS[day] ?? 'mon'}`)).join(' ')) +
                    (form.happyHour ? ` · ${t('pricing.rules.window', { from: form.start, to: form.end })}` : ''),
                },
                ...(isPlay || isConsole
                  ? []
                  : [{ key: 'min', label: t('pricing.form.minOrderShort'), value: form.minOrder.trim() === '' ? t('pricing.promotionFlow.noMinimum') : format.money(moneyFrom(form.minOrder, currency) ?? { amount: 0, currency }) }]),
                { key: 'on', label: t('pricing.promotionFlow.active'), value: form.active ? t('pricing.promotionFlow.onNow') : t('pricing.promotionFlow.offNow') },
              ]}
            />
          </section>
        )}

        <div className="console-form__actions console-form__actions--end">
          {step === 0 ? (
            <Button type="button" variant="ghost" onClick={onDone}>
              {t('common.cancel')}
            </Button>
          ) : (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
              {t('common.back')}
            </Button>
          )}
          <Button type="submit" variant="primary" busy={create.isPending || update.isPending}>
            {step < 3 ? t('common.next') : editing === undefined ? t('pricing.promotionFlow.create') : t('pricing.form.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * The rules a form state stands for — to tell whether an edit changed them. Play time sends no products,
 * channels, minimum order or first-order rule, whatever the hidden fields held: the server refuses them.
 */
function rulesOf(form: FormState, currency: string): PromotionRules {
  // A console offer (ADR-0034) has no cart rules: no kinds, products, channels, minimum or first order.
  if (form.kinds.includes(CONSOLE)) {
    return {
      appliesToKinds: [],
      productIds: [],
      daysOfWeek: [...form.days].sort((a, b) => a - b),
      startMinuteOfDay: form.happyHour ? minuteOfDay(form.start) : null,
      endMinuteOfDay: form.happyHour ? minuteOfDay(form.end) : null,
      venueIds: form.everywhere ? [] : [...form.venueIds],
      channels: [],
      minOrderAmount: null,
      firstOrderOnly: false,
    };
  }
  if (form.kinds.includes(PLAY)) {
    return {
      appliesToKinds: [PLAY],
      productIds: [],
      daysOfWeek: [...form.days].sort((a, b) => a - b),
      startMinuteOfDay: form.happyHour ? minuteOfDay(form.start) : null,
      endMinuteOfDay: form.happyHour ? minuteOfDay(form.end) : null,
      venueIds: form.everywhere ? [] : [...form.venueIds],
      channels: [],
      minOrderAmount: null,
      firstOrderOnly: false,
    };
  }
  return {
    appliesToKinds: form.kinds.filter((kind): kind is PromotionKind => kind !== CONSOLE),
    productIds: form.someProducts ? [...form.productIds] : [],
    daysOfWeek: [...form.days].sort((a, b) => a - b),
    startMinuteOfDay: form.happyHour ? minuteOfDay(form.start) : null,
    endMinuteOfDay: form.happyHour ? minuteOfDay(form.end) : null,
    venueIds: form.everywhere ? [] : [...form.venueIds],
    channels: [...form.channels],
    minOrderAmount: form.minOrder.trim() === '' ? null : moneyFrom(form.minOrder, currency),
    firstOrderOnly: form.firstOrderOnly,
  };
}

/**
 * A console offer's reward (ADR-0034): which kind, and the fields that kind takes — a percentage, a sum off each extra
 * controller, a bundle (controllers × minutes for a price), or free time (minutes paid, minutes free).
 */
function ConsoleRewardFields({
  form,
  set,
  currency,
  error,
}: {
  readonly form: FormState;
  readonly set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  readonly currency: string;
  readonly error: string | undefined;
}): ReactNode {
  const { t } = useTranslation('console');
  return (
    <>
      <fieldset className="console-choice-group">
        <legend className="clav-field__label">{t('pricing.console.form.reward')}</legend>
        <div className="console-chips">
          {CONSOLE_REWARDS.map((reward) => (
            <Chip key={reward} selected={form.reward === reward} onSelectedChange={() => set('reward', reward)}>
              {t(`pricing.console.reward.${reward}`)}
            </Chip>
          ))}
        </div>
        <p className="clav-field__description">{t(`pricing.console.rewardHint.${form.reward}`)}</p>
      </fieldset>
      {form.reward === 'extra_controllers_percent' || form.reward === 'session_percent' ? (
        <TextField label={t('pricing.form.percent')} required numeric inputMode="decimal" value={form.percent} onChange={(e) => set('percent', e.target.value)} error={error} />
      ) : null}
      {form.reward === 'extra_controllers_amount' ? (
        <TextField label={t('pricing.console.form.perController', { currency })} required numeric inputMode="decimal" value={form.amount} onChange={(e) => set('amount', e.target.value)} error={error} />
      ) : null}
      {form.reward === 'bundle' ? (
        <>
          <div className="console-form__row">
            <TextField label={t('pricing.console.form.controllers')} required numeric inputMode="numeric" value={form.bundleControllers} onChange={(e) => set('bundleControllers', e.target.value)} />
            <TextField label={t('pricing.console.form.minutes')} required numeric inputMode="numeric" value={form.rewardMinutes} onChange={(e) => set('rewardMinutes', e.target.value)} />
          </div>
          <TextField label={t('pricing.console.form.bundlePrice', { currency })} required numeric inputMode="decimal" value={form.amount} onChange={(e) => set('amount', e.target.value)} error={error} />
        </>
      ) : null}
      {form.reward === 'free_time' ? (
        <div className="console-form__row">
          <TextField label={t('pricing.console.form.paidMinutes')} required numeric inputMode="numeric" value={form.rewardMinutes} onChange={(e) => set('rewardMinutes', e.target.value)} error={error} />
          <TextField label={t('pricing.console.form.freeMinutes')} required numeric inputMode="numeric" value={form.freeMinutes} onChange={(e) => set('freeMinutes', e.target.value)} />
        </div>
      ) : null}
    </>
  );
}

function ProductChips({ products, selected, onToggle }: { readonly products: readonly MenuProduct[]; readonly selected: readonly string[]; readonly onToggle: (id: string) => void }): ReactNode {
  const { t } = useTranslation('console');
  if (products.length === 0) return <p className="console-subtle">{t('pricing.promotionFlow.noProducts')}</p>;
  return (
    <div className="console-chips">
      {products.map((product) => (
        <Chip key={product.id} selected={selected.includes(product.id)} onSelectedChange={() => onToggle(product.id)}>
          {product.name}
        </Chip>
      ))}
    </div>
  );
}
